import useSafeFetch from 'shared/hooks/useSafeFetch';
import React, { useState, useRef, useEffect } from 'react';

export interface UserType {
  username: string;
  uid: string;
  businessName: string;
  email: string;
  authorized: boolean;
  roles: string[];
}

interface UserContextType {
  user: UserType | undefined;
  error: boolean;
  loading: boolean;
  refreshUser: () => void;
}

export const UserContext = React.createContext<UserContextType>({
  user: undefined,
  error: false,
  loading: false,
  refreshUser: () => null
});

interface UserContextProviderState {
  user: UserType | undefined;
  error: boolean;
}

const UserContextProvider: React.FC = props => {
  const [state, setState] = useState<UserContextProviderState>({
    user: undefined,
    error: false
  });
  const { get } = useSafeFetch();

  const refreshUser = useRef(async () => {
    if (state.user !== undefined || state.error) {
      setState({ user: undefined, error: false });
    }
    try {
      const response = await get(`${process.env.REACT_APP_PORTAL}/me`, { redirectToLogin: false });
      if (response.ok) {
        const user = await response.json();
        setState({
          user: {
            username: user.username,
            businessName: user.name,
            uid: user.id,
            email: user.email,
            authorized: user.roles?.includes('cards'),
            roles: user.roles
          },
          error: false
        });
      }
    } catch (e) {
      if (!e.cancelled && !e.unauthenticated) {
        setState(s => ({ ...s, error: true }));
      } else {
        console.error(e);
      }
    }
  });

  useEffect(() => {
    refreshUser.current();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user: state.user,
        refreshUser: refreshUser.current,
        error: state.error,
        loading: state.user === undefined && !state.error
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
